/**
 * Toasts: confirmation that something saved, or that it did not.
 *
 * A toast is never the only place an error lives — a form error belongs on
 * its field, a load error in the region that failed. Toasts are for the
 * result of an action somebody just took.
 */
import { CheckCircle, Info, WarningOctagon, X } from '@phosphor-icons/react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

import { ui } from './text';

type ToastTone = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

export interface ToastApi {
  success: (message: string) => void;
  /** Stays twice as long, and is announced assertively. */
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const ICON = { success: CheckCircle, error: WarningOctagon, info: Info } as const;
const DURATION: Record<ToastTone, number> = { success: 4000, info: 5000, error: 8000 };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counter = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
  }, []);

  const schedule = useCallback(
    (id: number, tone: ToastTone) => {
      clearTimeout(timers.current.get(id));
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), DURATION[tone]),
      );
    },
    [dismiss],
  );

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = ++counter.current;

      // Four at most: a burst of failures should not wallpaper the screen.
      setToasts((current) => [...current.slice(-3), { id, tone, message }]);
      schedule(id, tone);
    },
    [schedule],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section className="toasts" aria-label={ui('notifications')}>
        {/* Two live regions: errors interrupt, everything else waits its turn. */}
        <div role="status" aria-live="polite" className="toasts-list">
          {toasts
            .filter((toast) => toast.tone !== 'error')
            .map((toast) => (
              <ToastView key={toast.id} toast={toast} onDismiss={dismiss} onHold={() => clearTimeout(timers.current.get(toast.id))} onRelease={() => schedule(toast.id, toast.tone)} />
            ))}
        </div>
        <div role="alert" aria-live="assertive" className="toasts-list">
          {toasts
            .filter((toast) => toast.tone === 'error')
            .map((toast) => (
              <ToastView key={toast.id} toast={toast} onDismiss={dismiss} onHold={() => clearTimeout(timers.current.get(toast.id))} onRelease={() => schedule(toast.id, toast.tone)} />
            ))}
        </div>
      </section>
    </ToastContext.Provider>
  );
}

function ToastView({ toast, onDismiss, onHold, onRelease }: { toast: ToastItem; onDismiss: (id: number) => void; onHold: () => void; onRelease: () => void }) {
  const Glyph = ICON[toast.tone];

  return (
    // Hovering or focusing a toast pauses its timer, so it cannot vanish mid-read.
    <div className="toast" data-tone={toast.tone} onMouseEnter={onHold} onMouseLeave={onRelease} onFocus={onHold} onBlur={onRelease}>
      <Glyph className="toast-icon" size={20} weight="fill" aria-hidden="true" />
      <p className="toast-message">{toast.message}</p>
      <button type="button" className="toast-close" aria-label={ui('dismiss')} onClick={() => onDismiss(toast.id)}>
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const value = useContext(ToastContext);

  if (value === null) throw new Error('useToast() outside <ToastProvider>');

  return value;
}
